import React, { useEffect, useRef, useState } from "react";
import { VideoPopover } from "./video-popover";
import { imageBuilder } from "../lib/helpers";
import getYouTubeID from "get-youtube-id";
import Vimeo from "@u-wave/react-vimeo";

const VimeoPopover = ({ node }) => {
  const [thumbUrl, setThumbUrl] = useState(null);

  useEffect(() => {
    if (node.previewImage || !node.vimeoId) return;
    fetch(`https://vimeo.com/api/oembed.json?url=https://vimeo.com/${node.vimeoId}&width=1280`)
      .then((r) => r.json())
      .then((data) => setThumbUrl(data.thumbnail_url || null))
      .catch(() => {});
  }, [node.vimeoId]);

  const image = node.previewImage;
  const mobileImage = image
    ? imageBuilder(image).quality(99).auto("format").url()
    : thumbUrl;
  const width = image ? Math.ceil(image.asset.metadata.dimensions.width / 2) : undefined;
  const height = image ? Math.ceil(image.asset.metadata.dimensions.height / 2) : undefined;

  return (
    <div className="justify-center my-8">
      <VideoPopover
        image={image}
        previewVideo={node.previewVideo}
        mobileImage={mobileImage}
        width={width}
        height={height}
        vimeoId={node.vimeoId}
        buttonText={node.previewCta}
        buttonBg={node.previewCta ? "bg-green-emerald" : null}
        buttonClass={node.previewCta ? "text-white border-white" : null}
      />
    </div>
  );
};

export const ExternalVideo = ({ node, inline = false }) =>
  node.file ? <SelfHostedVideo node={node} />
    :
    node.vimeoId ? (
      inline ? (
        <VimeoPlayer vimeoId={node.vimeoId} />
      ) : (
        <VimeoPopover node={node} />
      )
    ) : node.url ? (
      <div>
        <YouTubePlayer url={node.url} />
      </div>
    ) : node.loomUrl ? (
      <div className="justify-center my-8">
        <VideoPopover
          image={node.previewImage}
          previewVideo={node.previewVideo}
          mobileImage={
            node?.previewImage
              ? imageBuilder(node.previewImage).quality(99).auto("format").url()
              : null
          }
          width={Math.ceil(
            node.previewImage?.asset.metadata.dimensions.width / 2
          )}
          height={Math.ceil(
            node.previewImage?.asset.metadata.dimensions.height / 2
          )}
          loomUrl={node.loomUrl}
          buttonText={node.previewCta}
          buttonBg={node.previewCta ? "bg-transparent" : null}
          buttonClass={node.previewCta ? "text-white border-white" : null}
        />
      </div>
    ) : null;

export const YouTubePlayer = ({ url }) => (
  <div className="rounded-xl overflow-hidden" style={{ position: 'relative', paddingBottom: '56.25%', height: 0 }}>
    <iframe
      src={`https://www.youtube.com/embed/${getYouTubeID(url)}?controls=0&rel=0`}
      referrerPolicy="strict-origin-when-cross-origin"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      allowFullScreen
      style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }}
    />
  </div>
);

export const VimeoPlayer = ({ vimeoId }) => {
  const playerRef = useRef(null);

  return (
    <div className="vimeo-player-wrapper" style={{ position: 'relative', paddingBottom: '56.25%', height: 0 }}>
      <Vimeo
        video={vimeoId}
        ref={playerRef}
        showTitle={false}
        showByline={false}
        showPortrait={false}
        responsive={true}
        speed={true}
        textTrack="en-x-autogen"
        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
      />
    </div>
  );
};

const SelfHostedVideo = ({ node }) => {
  const videoRef = useRef(null);
  const url = node.file.asset.url;
  const isWebm = url?.split("?")[0].endsWith(".webm");

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.playbackRate = 1.3;
    }
  }, []);

  return (
    <div className="flex justify-center rounded-xl overflow-hidden">
      <video
        {...(isWebm ? { loop: true, autoPlay: true, muted: true, playsInline: true } : { controls: true })}
        className="w-full max-w-3xl"
        style={{
          maxHeight: "calc(100vh - 200px)",
        }}
        ref={videoRef}
      >
        <source src={url} type={isWebm ? "video/webm" : "video/mp4"} />
        Your browser does not support the video tag.
      </video>
    </div>
  );
};
